// =============================================================================
// FILE PATH : src/modules/pms/task_template/pages/TaskTemplatePage.js
// MODULE    : PMS → Task Template
// PURPOSE   : Main page for Task Template Master
// =============================================================================

import React, { useEffect, useState } from "react";

import TaskTemplateSummaryCards from "../components/TaskTemplateSummaryCards";
import TaskTemplateFilterBar from "../components/TaskTemplateFilterBar";
import TaskTemplateTable from "../components/TaskTemplateTable";
import TaskTemplateFormDrawer from "../components/TaskTemplateFormDrawer";

import {
  getTemplates,
  deleteTemplate,
  getEquipmentList
} from "../services/taskTemplateService";


// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------
const TaskTemplatePage = () => {

  const [equipmentList, setEquipmentList] = useState([]);

  const [templates, setTemplates] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [filterType, setFilterType] = useState("ALL");


  // ---------------------------------------------------------------------------
  // FETCH EQUIPMENT
  // ---------------------------------------------------------------------------
  const fetchEquipment = async () => {
    try {
      const res = await getEquipmentList();

      // API format: { success: true, data: [...] }
      setEquipmentList(res.data || []);

    } catch (err) {
      console.error("Equipment load error:", err);
    }
  };


  // ---------------------------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------------------------
  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await getTemplates();
      const data = res.data || [];

      setTemplates(data);
      setFiltered(data);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  // ---------------------------------------------------------------------------
  // INITIAL LOAD
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchData();
    fetchEquipment();   // 🔥 FIX: THIS WAS MISSING
  }, []);


  // ---------------------------------------------------------------------------
  // FILTER HANDLING (SUMMARY CARDS)
  // ---------------------------------------------------------------------------
  const handleCardFilter = (type) => {

    setFilterType(type);

    if (type === "ALL") {
      setFiltered(templates);
    } else if (type === "INACTIVE") {
      setFiltered(templates.filter(t => !t.is_active));
    } else {
      setFiltered(templates.filter(t => t.template_type === type));
    }
  };


  // ---------------------------------------------------------------------------
  // FILTER BAR
  // ---------------------------------------------------------------------------
  const handleFilterBar = ({ search, type }) => {

    let data = [...templates];

    if (search) {
      data = data.filter(t =>
        t.template_name.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (type) {
      data = data.filter(t => t.template_type === type);
    }

    setFiltered(data);
  };


  // ---------------------------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------------------------
  const handleDelete = async (row) => {

    const confirmDelete = window.confirm("Delete this template?");
    if (!confirmDelete) return;

    try {
      await deleteTemplate(row.id);
      fetchData();
    } catch (err) {
      alert("Error deleting template");
    }
  };


  // ---------------------------------------------------------------------------
  // EDIT
  // ---------------------------------------------------------------------------
  const handleEdit = (row) => {
    setEditData(row);
    setDrawerOpen(true);
  };


  // ---------------------------------------------------------------------------
  // ADD NEW
  // ---------------------------------------------------------------------------
  const handleAdd = () => {
    setEditData(null);
    setDrawerOpen(true);
  };


  // ---------------------------------------------------------------------------
  // SUCCESS (AFTER SAVE)
  // ---------------------------------------------------------------------------
  const handleSuccess = () => {
    fetchData();
  };


  return (
    <div style={{ padding: "20px" }}>

      {/* HEADER */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px"
      }}>
        <h2>Task Template Master</h2>

        <button
          onClick={handleAdd}
          style={{
            padding: "8px 14px",
            background: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          + Create Template
        </button>
      </div>


      {/* SUMMARY CARDS */}
      <TaskTemplateSummaryCards
        templates={templates}
        activeFilter={filterType}
        onFilterChange={handleCardFilter}
      />


      {/* FILTER BAR */}
      <TaskTemplateFilterBar
        onApplyFilters={handleFilterBar}
      />


      {/* TABLE */}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <TaskTemplateTable
          templates={filtered}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}


      {/* DRAWER */}
      <TaskTemplateFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleSuccess}
        editData={editData}
        equipmentList={equipmentList}   // 🔥 FIXED HERE
      />

    </div>
  );
};

export default TaskTemplatePage;